import * as Types from './types';
import * as FormTypes from '../forms/types';
import { LoanType } from '@apply/constants';

import { createAction } from '../utils';
import { updateManagementAgreement } from '../borrower/actions';

export const reset = () => createAction(Types.RESET);

export const addProperty = () => createAction(Types.ADD_PROPERTY);

export const removeProperty = (guid: string) => createAction(Types.REMOVE_PROPERTY, { guid });

export const updateStructureType = (
    guid: string,
    value: Types.NonRentalStructureType | Types.RentalStructureType,
    loanType: LoanType
) => createAction(Types.UPDATE_STRUCTURE_TYPE, { guid, value, loanType });

export const updateAddressLine1 = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_LINE1, { guid, value });

export const updateAddressLine2 = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_LINE2, { guid, value });

export const updateAddressCity = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_CITY, { guid, value });

export const updateAddressState = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_STATE, { guid, value });

export const updateAddressCounty = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_COUNTY, { guid, value });

export const updateAddressZipcode = (guid: string, value: string) => createAction(Types.UPDATE_ADDRESS_ZIPCODE, { guid, value });

// non-rental-specific
export const updateLegalDescription = (guid: string, value: string) =>
    createAction(Types.UPDATE_LEGAL_DESCRIPTION, { guid, value });

export const updateBudget = (guid: string, value: Types.Attachment) => createAction(Types.UPDATE_BUDGET, { guid, value });

export const updateLandBudget = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_LAND_BUDGET, { guid, value });

export const updateFixFlipBudget = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_FIXFLIP_BUDGET, { guid, value });

export const updatePlans = (guid: string, value: Types.Attachment) => createAction(Types.UPDATE_PLANS, { guid, value });

export const updateSpecSheet = (guid: string, value: Types.Attachment) => createAction(Types.UPDATE_SPEC_SHEET, { guid, value });

export const updateSurvey = (guid: string, value: Types.Attachment) => createAction(Types.UPDATE_SURVEY, { guid, value });

export const updatePresold = (guid: string, value: boolean) => createAction(Types.UPDATE_PRESOLD, { guid, value });

export const updateContactIsPrimary = (guid: string, value: Record<string, unknown>) =>
    createAction(Types.UPDATE_CONTACT_IS_PRIMARY, { guid, value });

export const updateNonRentalName = (guid: string, value: string) => createAction(Types.UPDATE_NON_RENTAL_NAME, { guid, value });

export const updateNonRentalEmail = (guid: string, value: string) => createAction(Types.UPDATE_NON_RENTAL_EMAIL, { guid, value });

export const updateNonRentalPhone = (guid: string, value: string) => createAction(Types.UPDATE_NON_RENTAL_PHONE, { guid, value });

export const updateConstructionStarted = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_CONSTRUCTION_STARTED, { guid, value });

export const updateIsAquisitionFinancing = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_IS_ACQUISITION_FINANCING, { guid, value });

export const updateRentalIsAquisitionFinancing = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_RENTAL_IS_ACQUISITION_FINANCING, { guid, value });

export const updateAnyExistingDebt = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_ANY_EXISTING_DEBT, { guid, value });

export const updatePresaleContract = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_PRESALE_CONTRACT, { guid, value });

export const updateLandSize = (guid: string, value: Types.LandSize) => createAction(Types.UPDATE_LAND_SIZE, { guid, value });

// rental specific
export const updateRentalIsLoanProceedsUsedPropertyImprovements = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_RENTAL_IS_LOAN_PROCEEDS_USED_PROPERTY_IMPROVEMENTS, { guid, value });

export const updateUnitNumber = (guid: string, value: string) => createAction(Types.UPDATE_UNIT_NUMBER, { guid, value });

export const updatePurchasedLessThanTwoYearsAgo = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_PURCHASED_LESS_THAN_TWO_YEAR_AGO, {
        guid,
        value,
    });

export const updateMoreThanTenKExpenditures = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_MORE_THAN_TENK_EXPENDITURES, { guid, value });

export const updateCurrentDebt = (guid: string, value: number) => createAction(Types.UPDATE_CURRENT_DEBT, { guid, value });

export const onChangeNonRentalCurrentDebt = (guid: string, value: number) =>
    createAction(Types.UPDATE_NONRENTAL_CURRENT_DEBT, { guid, value });

export const updateNumberRentalUnits = (guid: string, value: number) =>
    createAction(Types.UPDATE_NUMBER_RENTAL_UNITS, { guid, value });

export const updateNumberConstructionUnits = (guid: string, value: number) =>
    createAction(Types.UPDATE_NUMBER_CONSTRUCTION_UNITS, { guid, value });

export const updateNumberUnitsIncomeRestricted = (guid: string, value: number) =>
    createAction(Types.UPDATE_NUMBER_UNITS_INCOME_RESTRICTED, { guid, value });

export const updateRentalNumberUnitsIncomeRestricted = (guid: string, value: number) =>
    createAction(Types.UPDATE_RENTAL_NUMBER_UNITS_INCOME_RESTRICTED, {
        guid,
        value,
    });

export const updatePurchaseContract = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_PURCHASE_CONTRACT, { guid, value });

export const updateCapitalExpenditures = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_CAPITAL_EXPENDITURES, { guid, value });

export const updateRentalUnit = (guid: string, value: Types.RentalUnit) =>
    createAction(Types.UPDATE_RENTAL_UNIT, { guid, value });

export const updateProvideRentalUnitsLater = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_PROVIDE_RENTAL_UNITS_LATER, { guid, value });

export const replacePropertiesState = (properties: Types.PropertiesState) =>
    createAction(Types.REPLACE_PROPERTIES_STATE, { properties });

export const updateIsDirty = (isDirty: boolean) => createAction(Types.UPDATE_IS_DIRTY, { isDirty });

export const updateCurrentPropertyId = (currentPropertyId: string) =>
    createAction(Types.UPDATE_CURRENT_PROPERTY_ID, { currentPropertyId });

export const saveForm = (form: FormTypes.FormState) => createAction(Types.SAVE_FORM, { form });

export const updateGeneralContractorResume = (guid: string, value: Types.Attachment) =>
    createAction(Types.UPDATE_GENERAL_CONTRATOR_RESUME, { guid, value });

export const updateGeneralContractorName = (guid: string, value: string) =>
    createAction(Types.UPDATE_GENERAL_CONTRATOR_NAME, { guid, value });

export const updateGeneralContractorWillWork = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_GENERAL_CONTRATOR_WILL_WORK, { guid, value });

export const updateYearBuilt = (guid: string, value: number) => createAction(Types.UPDATE_UPDATE_YEAR_BUILT, { guid, value });

export const updateRentalYearBuilt = (guid: string, value: number) =>
    createAction(Types.UPDATE_RENTAL_YEAR_BUILT, { guid, value });

export const updateBuildType = (guid: string, value: number) => createAction(Types.UPDATE_UPDATE_BUILD_TYPE, { guid, value });

export const updateRentalBuildType = (guid: string, value: number) =>
    createAction(Types.UPDATE_RENTAL_BUILD_TYPE, { guid, value });

export const updateBuildRentType = (guid: string, value: number) =>
    createAction(Types.UPDATE_RENTAL_BUILDRENT_TYPE, { guid, value });

export const updateUndergroundStorageTanks = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_UNDERGROUND_STORAGE_TANKS, { guid, value });

export const updateAcquisitionDate = (guid: string, value: string) =>
    createAction(Types.UPDATE_ACQUISITIONS_DATE, { guid, value });

export const updatePurchaseDate = (guid: string, value: string) => createAction(Types.UPDATE_PURCHASE_DATE, { guid, value });

export const updateIsForeclosure = (guid: string, value: boolean) => createAction(Types.UPDATE_IS_FORECLOSURE, { guid, value });

export const updateNeedFundingAtPurchase = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_NEED_FUNDING_AT_PURCHASE, { guid, value });

export const updateIntendedWork = (guid: string, value: string) => createAction(Types.UPDATE_INTENDED_WORK, { guid, value });

export const updateExpectedListingPrice = (guid: string, value: number) =>
    createAction(Types.UPDATE_EXPECTED_LISTING_PRICE, { guid, value });

export const updateTargetLoanRequest = (guid: string, value: number) =>
    createAction(Types.UPDATE_TARGET_LOAN_REQUEST, { guid, value });

export const updateRentalTargetLoanRequest = (guid: string, value: number) =>
    createAction(Types.UPDATE_RENTAL_TARGET_LOAN_REQUEST, { guid, value });

export const updatePreferredTermRequest = (guid: string, value: number) =>
    createAction(Types.UPDATE_PREFERRED_TERM_REQUEST, { guid, value });

export const updateCurrentPlattedLots = (guid: string, value: number) =>
    createAction(Types.UPDATE_CURRENT_PLATTED_LOTS, { guid, value });

export const updatePlannedPlattedLots = (guid: string, value: number) =>
    createAction(Types.UPDATE_PLANNED_PLATTED_LOTS, { guid, value });

export const updateRequiredZoningOrEntitlement = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_REQUIRED_ZONING_OR_ENTITLEMENT, { guid, value });

export const updateDescribeRequiredInspections = (guid: string, value: string) =>
    createAction(Types.UPDATE_DESCRIBE_REQUIRED_INPSECTIONS, { guid, value });

export const updateWillBuildOnLandYourself = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_WILL_BUILD_ON_LAND_YOURSELF, { guid, value });

export const updateExitPlans = (guid: string, value: string) => createAction(Types.UPDATE_EXIT_PLANS, { guid, value });

export const updateStartedSiteWork = (guid: string, value: boolean) =>
    createAction(Types.UPDATE_STARTED_SITE_WORK, { guid, value });

export type PropertyActionReturns =
    | ReturnType<typeof reset>
    | ReturnType<typeof addProperty>
    | ReturnType<typeof removeProperty>
    | ReturnType<typeof updateStructureType>
    | ReturnType<typeof updateAddressLine1>
    | ReturnType<typeof updateAddressLine2>
    | ReturnType<typeof updateAddressCity>
    | ReturnType<typeof updateAddressState>
    | ReturnType<typeof updateAddressZipcode>
    | ReturnType<typeof updateLegalDescription>
    | ReturnType<typeof updateBudget>
    | ReturnType<typeof updatePlans>
    | ReturnType<typeof updateSpecSheet>
    | ReturnType<typeof updateSurvey>
    | ReturnType<typeof updatePresaleContract>
    | ReturnType<typeof updatePresold>
    | ReturnType<typeof updateConstructionStarted>
    | ReturnType<typeof updateLandSize>
    | ReturnType<typeof updateUnitNumber>
    | ReturnType<typeof updatePurchasedLessThanTwoYearsAgo>
    | ReturnType<typeof updateMoreThanTenKExpenditures>
    | ReturnType<typeof updateCurrentDebt>
    | ReturnType<typeof updateNumberRentalUnits>
    | ReturnType<typeof updatePurchaseContract>
    | ReturnType<typeof updateCapitalExpenditures>
    | ReturnType<typeof updateRentalUnit>
    | ReturnType<typeof updateProvideRentalUnitsLater>
    | ReturnType<typeof replacePropertiesState>
    | ReturnType<typeof updateIsDirty>
    | ReturnType<typeof updateCurrentPropertyId>
    | ReturnType<typeof saveForm>
    | ReturnType<typeof updateGeneralContractorName>
    | ReturnType<typeof updateGeneralContractorResume>
    | ReturnType<typeof updateGeneralContractorWillWork>;

export type AttachmentUpdater =
    | typeof updateBudget
    | typeof updatePlans
    | typeof updateSpecSheet
    | typeof updateSurvey
    | typeof updatePresaleContract
    | typeof updatePurchaseContract
    | typeof updateCapitalExpenditures
    | typeof updateGeneralContractorResume
    | typeof updateManagementAgreement;
